// src/services/metricsService.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://riskassessplus.com/api';

class MetricsService {
    // Handle 401 responses — clear auth and redirect to login
    _handleUnauthorized(response) {
        if (response.status === 401) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('user');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
            throw new Error('Session expired. Please log in again.');
        }
    }

    // Get all metrics (public - no auth required)
    async getMetrics(params = {}) {
        const queryParams = new URLSearchParams({
            skip: params.skip || 0,
            limit: params.limit || 100
        });

        try {
            const response = await fetch(`${API_BASE_URL}/metrics/?${queryParams}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching metrics:', error);
            throw error;
        }
    }

    // Get metrics summary (public)
    async getMetricsSummary() {
        try {
            const response = await fetch(`${API_BASE_URL}/metrics/summary`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching metrics summary:', error);
            throw error;
        }
    }

    // Get single metric by ID (public)
    async getMetricById(metricId) {
        try {
            const response = await fetch(`${API_BASE_URL}/metrics/${metricId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching metric:', error);
            throw error;
        }
    }

    // Create metric (admin only)
    async createMetric(metricData, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/metrics/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(metricData)
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error creating metric:', error);
            throw error;
        }
    }

    // Update metric (admin only)
    async updateMetric(metricId, metricData, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/metrics/${metricId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(metricData)
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                let detail;
                if (typeof errorData.detail === 'string') {
                    detail = errorData.detail;
                } else if (Array.isArray(errorData.detail)) {
                    detail = errorData.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join('; ');
                } else {
                    detail = `HTTP error! status: ${response.status}`;
                }
                throw new Error(detail);
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating metric:', error);
            throw error;
        }
    }

    // Delete metric (admin only)
    async deleteMetric(metricId, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/metrics/${metricId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error deleting metric:', error);
            throw error;
        }
    }

    // Get metrics start date (public)
    async getStartDate() {
        try {
            const response = await fetch(`${API_BASE_URL}/metrics/start-date`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Error fetching start date:', error);
            throw error;
        }
    }

    // Update metrics start date (admin only)
    async updateStartDate(startDate, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/metrics/start-date`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ start_date: startDate })
            });
            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const detail = typeof errorData.detail === 'string'
                    ? errorData.detail
                    : JSON.stringify(errorData.detail) || `HTTP error! status: ${response.status}`;
                throw new Error(detail);
            }
            return await response.json();
        } catch (error) {
            console.error('Error updating start date:', error);
            throw error;
        }
    }

    // --- Visit tracking (unified, replaces old click/impression tracking) ---

    _getVisitorId() {
        let id = localStorage.getItem('riskas_visitor_id');
        if (!id) {
            id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            localStorage.setItem('riskas_visitor_id', id);
        }
        return id;
    }

    _getSessionId() {
        let id = sessionStorage.getItem('riskas_session_id');
        if (!id) {
            id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            sessionStorage.setItem('riskas_session_id', id);
        }
        return id;
    }

    /**
     * Track a user visit/action. Fire-and-forget.
     * @param {string} actionType - 'page_view', 'click', 'feature_use', 'session_end'
     * @param {object} extra - { feature_name, session_duration, user_id }
     */
    trackVisit(actionType, extra = {}) {
        const token = localStorage.getItem('accessToken');
        let userId = extra.user_id || null;
        // Try to extract user_id from JWT if not provided
        if (!userId && token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                userId = parseInt(payload.sub) || null;
            } catch (_) { /* ignore */ }
        }
        const body = {
            visitor_id: this._getVisitorId(),
            session_id: this._getSessionId(),
            user_id: userId,
            action_type: actionType,
            feature_name: extra.feature_name || null,
            session_duration: extra.session_duration || null
        };
        fetch(`${API_BASE_URL}/metrics/track-visit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        }).catch(() => {}); // silent fail — tracking should never block UI
    }

    // Convenience wrappers
    trackClick() { this.trackVisit('click'); }
    trackImpression() { this.trackVisit('page_view'); }
    trackFeature(featureName) { this.trackVisit('feature_use', { feature_name: featureName }); }
    trackSessionEnd(durationMinutes) { this.trackVisit('session_end', { session_duration: durationMinutes }); }
}

export default new MetricsService();
